package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.TreeCorrectionRequest
import fr.frux.shared.dto.TreeDto
import fr.frux.shared.dto.TreeRevisionsResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlin.uuid.Uuid

/**
 * Corrections d'identité et historique d'un arbre, contrat réseau séparé de [TreeApi] pour tenir
 * le seuil detekt de fonctions par classe.
 */
interface TreeCorrectionApi {
    /**
     * Corrige un champ d'identité (spec 2026-10-19, modèle wiki), ouvert à tout compte connecté.
     * Rend la fiche à jour. [idempotencyKey] : l'appelant la tire à l'ouverture de la feuille pour
     * qu'un « Réessayer » ne double rien. 400 MOVE_TOO_FAR au-delà de 50 m.
     */
    suspend fun correctTree(
        id: Long,
        request: TreeCorrectionRequest,
        idempotencyKey: String = Uuid.random().toString(),
    ): TreeDto

    /** Historique public : corrections de la plus récente à la plus ancienne, puis la création. */
    suspend fun getTreeRevisions(id: Long): TreeRevisionsResponse
}

/**
 * Implementation of TreeCorrectionApi using Ktor HttpClient.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class TreeCorrectionApiImpl(
    private val httpClient: HttpClient,
) : TreeCorrectionApi {
    override suspend fun correctTree(
        id: Long,
        request: TreeCorrectionRequest,
        idempotencyKey: String,
    ): TreeDto =
        httpClient
            .post("${ApiPaths.V1}/trees/$id/corrections") {
                header("Idempotency-Key", idempotencyKey)
                contentType(ContentType.Application.Json)
                // Type scellé explicite : sérialiser la sous-classe ferait perdre le discriminant `kind`.
                setBody<TreeCorrectionRequest>(request)
            }.body()

    override suspend fun getTreeRevisions(id: Long): TreeRevisionsResponse =
        httpClient.get("${ApiPaths.V1}/trees/$id/revisions").body()
}
